"use client"

import React from 'react'
import { motion } from 'framer-motion'
import { cn } from '../../../lib/utils'
import { WidgetBase, WidgetBaseProps } from '../core/widget-base'
import { 
  TrendingUp,
  TrendingDown,
  BarChart3,
  LineChart,
  PieChart,
  Activity,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react'
import { useSubscription } from '../../../hooks/use-subscription'
import { ProLockScreen } from '../../shared/pro-lock-screen'

export interface ChartDataPoint {
  label: string
  value: number
  percentage?: number
  color?: string
  meta?: Record<string, any>
}

export interface ChartSeries {
  name: string
  data: ChartDataPoint[]
  color?: string
  type?: 'line' | 'bar' | 'area'
}

export interface WidgetChartData {
  series?: ChartSeries[]
  categories?: string[]
  values?: number[]
  labels?: string[]
  total?: number
  change?: {
    value: number
    type: 'increase' | 'decrease' | 'neutral'
    period?: string
  }
}

export interface ChartWidgetProps extends Omit<Partial<WidgetBaseProps>, 'variant'> {
  data: WidgetChartData
  title?: string
  type?: 'line' | 'bar' | 'area' | 'pie' | 'donut' | 'mixed'
  variant?: 'default' | 'minimal' | 'detailed'
  backgroundVariant?: 'default' | 'minimal' | 'glass' | 'bordered' | 'gradient' | 'colored' | 'overlay' | 'floating'
  gradientDirection?: 'to-t' | 'to-tr' | 'to-r' | 'to-br' | 'to-b' | 'to-bl' | 'to-l' | 'to-tl'
  height?: number
  showLegend?: boolean
  showGrid?: boolean
  showTooltip?: boolean
  showDataLabels?: boolean
  animate?: boolean

  colors?: string[]
  onDataPointClick?: (point: ChartDataPoint) => void
}

export function ChartWidget({
  data,
  title,
  type = 'line',
  variant = 'default',
  backgroundVariant,
  gradientDirection,
  height = 300,
  showLegend = true,
  showGrid = true,
  showTooltip = true,
  showDataLabels = false,
  animate = true,

  colors,
  onDataPointClick,
  ...widgetProps
}: ChartWidgetProps) {
  const { hasProAccess, isLoading } = useSubscription();
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null)

  // Default colors - More vibrant palette
  const defaultColors = [
    '#3b82f6', // blue
    '#10b981', // emerald
    '#f59e0b', // amber
    '#ef4444', // red
    '#8b5cf6', // violet
    '#ec4899', // pink
    '#14b8a6', // teal
    '#f97316', // orange
    '#06b6d4', // cyan
    '#a855f7', // purple
  ]

  const chartColors = colors || defaultColors

  // Get max value for scaling
  const maxValue = React.useMemo(() => {
    if (data.values) {
      return Math.max(...data.values)
    }
    if (data.series) {
      return Math.max(...data.series.flatMap(s => s.data.map(d => d.value)))
    }
    return 100
  }, [data])

  // Format number with max 2 decimal places
  const formatNumber = (value: number): string => {
    if (value % 1 !== 0) {
      return (Math.round(value * 100) / 100).toString()
    }
    return value.toString()
  }

  // Render simple bar chart
  const renderBarChart = () => {
    const values = data.values || []
    const labels = data.labels || data.categories || []

    return (
      <div className="relative h-full overflow-hidden">
        <div className="absolute inset-0 flex items-end justify-between gap-1 pl-10 pr-2 pb-6">
          {values.map((value, index) => {
            const percentage = (value / maxValue) * 100
            const isHovered = hoveredIndex === index

            return (
              <motion.div
                key={index}
                className="relative flex flex-col items-center justify-end min-w-0"
                style={{ height: 'calc(100% - 24px)', flex: '1 1 0' }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                {/* Data labels shown on bars when enabled */}
                {showDataLabels && (
                  <motion.span
                    className="absolute text-xs font-medium text-white mix-blend-difference"
                    style={{
                      bottom: `calc(${percentage}% / 2)`,
                      left: '50%',
                      transform: 'translateX(-50%)'
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.1 + 0.6 }}
                  >
                    {formatNumber(value)}
                  </motion.span>
                )}
                <div className="relative h-full w-full flex flex-col justify-end">
                  <motion.div
                    className={cn(
                      "w-full rounded-t cursor-pointer transition-all duration-200",
                      "shadow-lg",
                      isHovered && "brightness-110 scale-y-105"
                    )}
                    style={{
                      background: `linear-gradient(to top, ${chartColors[index % chartColors.length]}, ${chartColors[index % chartColors.length]}dd)`,
                      minHeight: '2px',
                      boxShadow: isHovered ? `0 4px 20px ${chartColors[index % chartColors.length]}40` : 'none'
                    }}
                    initial={{ height: 0 }}
                    animate={{ height: `${percentage}%` }}
                    transition={{ 
                      duration: animate ? 0.6 : 0, 
                      delay: index * 0.1,
                      ease: "easeOut"
                    }}
                    onClick={() => onDataPointClick?.({ label: labels[index], value })}
                  />
                </div>
              </motion.div>
            )
          })}
        </div>
        
        {/* Tooltips - centered vertically and horizontally on bar */}
        {hoveredIndex !== null && (() => {
          const barWidth = 85 / values.length // Total width percentage divided by number of bars
          const barCenterX = 10 + (hoveredIndex * barWidth) + (barWidth / 2) // X position at center of bar
          
          return (
            <motion.div
              className="absolute bg-background border-2 rounded-lg px-3 py-2 text-xs pointer-events-none shadow-xl"
              style={{
                left: `${barCenterX}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 100
              }}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.15 }}
            >
              <div className="font-medium text-foreground">{labels[hoveredIndex] || `Item ${hoveredIndex + 1}`}</div>
              <div className="text-primary font-bold text-sm mt-0.5">{formatNumber(values[hoveredIndex])}</div>
            </motion.div>
          )
        })()}
        
        {/* Labels below bars */}
        <div className="absolute bottom-0 left-10 right-0 flex justify-between gap-1 pr-2 h-6">
          {labels.map((label, index) => (
            <div key={index} className="flex-1 flex justify-center">
              <span className="text-xs text-muted-foreground truncate">
                {label}
              </span>
            </div>
          ))}
        </div>

        {/* Grid lines - behind bars */}
        {showGrid && (
          <div className="absolute inset-0 pb-6 pointer-events-none">
            {[0, 25, 50, 75, 100].map(percentage => (
              <div
                key={percentage}
                className="absolute left-10 right-0 border-b border-muted/20"
                style={{ bottom: `calc(24px + ${percentage}% * (100% - 24px) / 100)` }}
              />
            ))}
            {/* Y-axis labels */}
            <div className="absolute left-0 top-0 bottom-6 w-9 flex flex-col justify-between text-xs text-muted-foreground pr-1">
              <span className="text-right">{formatNumber(maxValue)}</span>
              <span className="text-right">{formatNumber(maxValue * 0.75)}</span>
              <span className="text-right">{formatNumber(maxValue * 0.5)}</span>
              <span className="text-right">{formatNumber(maxValue * 0.25)}</span>
              <span className="text-right">0</span>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Render simple line chart
  const renderLineChart = () => {
    const values = data.values || []
    const labels = data.labels || data.categories || []
    
    if (values.length < 2) return null

    // Smaller padding for better space utilization
    const padding = 5 // percentage padding
    const chartWidth = 100 - (padding * 2)
    const chartHeight = 100 - (padding * 2)

    const points = values.map((value, index) => {
      const x = padding + (index / (values.length - 1)) * chartWidth
      const y = padding + (chartHeight - ((value / maxValue) * chartHeight))
      return { x, y, value, label: labels[index] }
    })

    const pathData = points
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
      .join(' ')

    return (
      <div className="relative h-full overflow-hidden">
        <svg
          className="w-full h-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          {/* Gradient definitions */}
          <defs>
            <linearGradient id="chartLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={chartColors[0]} stopOpacity="1" />
              <stop offset="50%" stopColor={chartColors[1] || chartColors[0]} stopOpacity="1" />
              <stop offset="100%" stopColor={chartColors[2] || chartColors[0]} stopOpacity="1" />
            </linearGradient>
            <linearGradient id="chartAreaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={chartColors[0]} stopOpacity="0.3" />
              <stop offset="50%" stopColor={chartColors[0]} stopOpacity="0.15" />
              <stop offset="100%" stopColor={chartColors[0]} stopOpacity="0.05" />
            </linearGradient>
          </defs>
          {/* Grid - Minimal style */}
          {showGrid && (
            <g className="opacity-10">
              {/* Only horizontal lines for cleaner look */}
              {[25, 50, 75].map(percentage => {
                const y = 5 + (percentage / 100) * 90
                return (
                  <line
                    key={percentage}
                    x1="0"
                    y1={y}
                    x2="100"
                    y2={y}
                    stroke="currentColor"
                    strokeWidth="0.3"
                  />
                )
              })}
            </g>
          )}

          {/* Line */}
          <motion.path
            d={pathData}
            fill="none"
            stroke="url(#chartLineGradient)"
            strokeWidth="0.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: animate ? 1 : 0 }}
          />

          {/* Area fill */}
          {type === 'area' && (
            <motion.path
              d={`${pathData} L ${90} ${90} L ${10} ${90} Z`}
              fill="url(#chartAreaGradient)"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: animate ? 0.5 : 0, delay: 0.5 }}
            />
          )}

          {/* Data points */}
          {points.map((point, index) => (
            <motion.circle
              key={index}
              cx={point.x}
              cy={point.y}
              r="0.8"
              fill={chartColors[0]}
              stroke="white"
              strokeWidth="0.3"
              initial={{ r: 0, opacity: 0 }}
              animate={{ 
                r: hoveredIndex === index ? 1.2 : 0.8, 
                opacity: 1,
                strokeWidth: hoveredIndex === index ? 0.5 : 0.3
              }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              className="cursor-pointer"
              style={{ 
                shapeRendering: 'geometricPrecision'
              }}
              onClick={() => onDataPointClick?.(point)}
              transition={{ duration: 0.2 }}
            />
          ))}
        </svg>

        {/* Labels */}
        {showTooltip && hoveredIndex !== null && points[hoveredIndex] && (() => {
          const point = points[hoveredIndex]
          // If point is in top 30% of chart, show tooltip below
          const showBelow = point.y < 30
          
          return (
            <div
              className="absolute bg-background border rounded px-2 py-1 text-xs pointer-events-none z-10 shadow-lg"
              style={{
                left: `${Math.min(90, Math.max(10, point.x))}%`,
                top: showBelow ? `${point.y}%` : `${point.y}%`,
                transform: showBelow 
                  ? 'translate(-50%, 10px)' 
                  : 'translate(-50%, calc(-100% - 10px))'
              }}
            >
              <div className="font-medium">{point.label}</div>
              <div>{formatNumber(point.value)}</div>
            </div>
          )
        })()}
      </div>
    )
  }

  // Render simple pie chart
  const renderPieChart = () => {
    const values = data.values || []
    const labels = data.labels || data.categories || []
    const total = values.reduce((sum, val) => sum + val, 0)
    
    let currentAngle = -90 // Start from top

    return (
      <div className="flex items-center justify-between gap-4 h-full overflow-hidden">
        <div className="relative w-48 h-48 flex-shrink-0">
          <svg className="w-full h-full transform" viewBox="0 0 100 100">
            {values.map((value, index) => {
              const percentage = (value / total) * 100
              const angle = (percentage / 100) * 360
              const startAngle = currentAngle
              const endAngle = currentAngle + angle
              
              const start = polarToCartesian(50, 50, 40, startAngle)
              const end = polarToCartesian(50, 50, 40, endAngle)
              const largeArcFlag = angle > 180 ? 1 : 0
              
              const pathData = [
                `M 50 50`,
                `L ${start.x} ${start.y}`,
                `A 40 40 0 ${largeArcFlag} 1 ${end.x} ${end.y}`,
                `Z`
              ].join(' ')
              
              currentAngle = endAngle
              
              return (
                <motion.path
                  key={index}
                  d={pathData}
                  fill={chartColors[index % chartColors.length]}
                  initial={{ scale: 0 }}
                  animate={{ scale: hoveredIndex === index ? 1.05 : 1 }}
                  style={{ transformOrigin: '50px 50px' }}
                  transition={{ duration: animate ? 0.3 : 0, delay: index * 0.05 }}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  className="cursor-pointer"
                  onClick={() => onDataPointClick?.({ label: labels[index], value, percentage })}
                />
              )
            })}
            
            {/* Center hole for donut */}
            {type === 'donut' && (
              <circle cx="50" cy="50" r="25" fill="var(--background)" />
            )}
          </svg>
          
          {/* Center text for donut */}
          {type === 'donut' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-2xl font-bold">{total}</div>
              <div className="text-xs text-muted-foreground">Total</div>
            </div>
          )}
        </div>

        {/* Legend */}
        {showLegend && (
          <div className="space-y-1 overflow-y-auto flex-1 min-w-0">
            {values.map((value, index) => {
              const percentage = ((value / total) * 100).toFixed(1)
              return (
                <div
                  key={index}
                  className={cn(
                    "flex items-center gap-2 cursor-pointer",
                    hoveredIndex === index && "opacity-80"
                  )}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <div
                    className="w-2 h-2 rounded flex-shrink-0"
                    style={{ backgroundColor: chartColors[index % chartColors.length] }}
                  />
                  <span className="text-xs truncate">{labels[index]}</span>
                  <span className="text-xs text-muted-foreground ml-auto">
                    {percentage}%
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  // Helper function for polar coordinates
  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees * Math.PI) / 180
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians)
    }
  }

  // Render chart based on type
  const renderChart = () => {
    switch (type) {
      case 'bar':
        return renderBarChart()
      case 'line':
      case 'area':
        return renderLineChart()
      case 'pie':
      case 'donut':
        return renderPieChart()
      default:
        return renderBarChart()
    }
  }

  // Pro access check - moved to the end after all hooks
  if (!isLoading && !hasProAccess) {
    return (
      <ProLockScreen 
        componentName="Chart Widget" 
        compact={true}
      />
    );
  }

  return (
    <WidgetBase
      title={title}
      variant={backgroundVariant}
      gradientDirection={gradientDirection}
      {...widgetProps}
    >
      <div className="space-y-4">
        {/* Summary stats */}
        {data.total !== undefined && (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="text-2xl font-bold">{formatNumber(data.total)}</p>
            </div>
            {data.change && (
              <div className={cn(
                "flex items-center gap-1",
                data.change.type === 'increase' ? 'text-green-600' : 
                data.change.type === 'decrease' ? 'text-red-600' : 
                'text-gray-600'
              )}>
                {data.change.type === 'increase' ? 
                  <ArrowUpRight className="h-4 w-4" /> : 
                  data.change.type === 'decrease' ?
                  <ArrowDownRight className="h-4 w-4" /> :
                  null
                }
                <span className="font-medium">
                  {data.change.type !== 'neutral' && (data.change.type === 'increase' ? '+' : '-')}
                  {Math.abs(data.change.value)}%
                </span>
                {data.change.period && (
                  <span className="text-xs text-muted-foreground">
                    vs {data.change.period}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Chart */}
        <div className="overflow-hidden" style={{ height: `${height}px` }}>
          {renderChart()}
        </div>
      </div>
    </WidgetBase>
  )
}

export default ChartWidget